import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useJobNotifications } from '@/hooks/useJobNotifications';
import { cn } from '@/lib/utils';

/**
 * Root layout: sidebar + top bar + main content.
 *
 * Responsive behaviour:
 * - md+: sidebar always visible
 * - below md: sidebar hidden by default, toggled via menu button
 *
 * WCAG 2.1 AA:
 * - Skip link to #main-content (visually hidden, appears on focus)
 * - <main> with id="main-content" and role="main"
 * - Sidebar wrapped in <nav aria-label="Main navigation"> (inside Sidebar)
 */
export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Background job-completion notifications: any job submitted from this
  // session is watched, and a toast surfaces when the AI pipeline finishes.
  useJobNotifications();

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Skip link — WCAG 2.1 AA SC 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-background focus:px-4 focus:py-2 focus:shadow-md focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={() => { setSidebarOpen(false); }}
        />
      )}

      {/* Sidebar — desktop: always visible; mobile: slide-in */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 transition-transform duration-200 md:relative md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Application sidebar"
      >
        <Sidebar className="h-full" />
      </aside>

      {/* Main content column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar />

        {/* Mobile menu toggle */}
        <button
          type="button"
          aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={sidebarOpen}
          aria-controls="sidebar"
          onClick={() => { setSidebarOpen((prev) => !prev); }}
          className={cn(
            'fixed bottom-4 left-4 z-40 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg md:hidden',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          {sidebarOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
        </button>

        <main
          id="main-content"
          role="main"
          className="flex-1 overflow-y-auto p-6"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
